import { Controller } from "@hotwired/stimulus"
import consumer from "../channels/consumer"

// Connects to data-controller="terminal-status-subscription"
export default class extends Controller {
  static targets = ["terminalCard", "badgeStatus"]
  static values = {
    terminalId: String
  }

  connect() {
    console.log("TerminalStatusSubscriptionController connected")
    console.log("Terminal ID:", this.terminalIdValue)
    
    // Expose controller globally for debugging
    window.terminalDebug = this
    
    // Listen for terminal change events
    this.element.addEventListener('terminal:changed', this.terminalChanged.bind(this))
    
    // Handle hardware buttons based on terminal selection
    this.updateHardwareButtons()
    
    // S'abonner au canal de mise à jour des statuts des terminaux
    this.subscription = consumer.subscriptions.create(
      { channel: "TerminalsStatusChannel" },
      {
        connected: () => {
          console.log("Connected to TerminalsStatusChannel")
        },
        
        disconnected: () => {
          console.log("Disconnected from TerminalsStatusChannel")
        },
        
        received: (data) => {
          console.log("Received terminal status update:", data)
          this.updateTerminalUI(data)
          // Also update the current terminal status in navbar if it matches
          this.updateCurrentTerminalStatus(data)
        }
      }
    )
    
    // Create hub status subscription for individual terminal monitoring
    this.createHubStatusSubscription()
    
    // Try to get initial status from server-side data
    this.initializeFromServerData()
  }

  // Initialize badge status from server-side rendered data if available
  initializeFromServerData() {
    if (!this.terminalIdValue || this.terminalIdValue.length === 0) {
      console.log("No terminal ID for server data initialization")
      return
    }
    
    // Check if any badge indicators already have a non-gray status (indicating server knew the status)
    this.badgeStatusTargets.forEach(indicator => {
      if (indicator.classList.contains('bg-green-500')) {
        console.log("Server data indicates terminal is ready")
        // Already set to ready, no need to change
      } else if (indicator.classList.contains('bg-yellow-500')) {
        console.log("Server data indicates terminal has errors")
        // Already set to error, no need to change
      } else {
        console.log("Server data indicates unknown status, waiting for WebSocket...")
        // Keep the pulse animation until WebSocket updates arrive
      }
    })
  }

  // Request immediate status update for faster initial display
  requestImmediateStatus() {
    if (!this.terminalIdValue || this.terminalIdValue.length === 0) {
      console.log("No terminal ID, skipping immediate status request")
      return
    }
    
    // Instead of a fetch request, just wait for the WebSocket connection
    // The hub should send status immediately upon connection
    console.log("Waiting for WebSocket status update...")
  }

  disconnect() {
    if (this.subscription) {
      this.subscription.unsubscribe()
      console.log("Unsubscribed from TerminalsStatusChannel")
    }
    
    if (this.hubStatusSubscription) {
      this.hubStatusSubscription.unsubscribe()
    }
  }

  // Called when the terminal selection changes
  terminalChanged(event) {
    const newTerminalId = event.detail.terminalId
    console.log("Terminal controller notified of terminal change:", newTerminalId)
    
    // Update our terminal ID value
    this.terminalIdValue = newTerminalId
    
    // Update hardware buttons state
    this.updateHardwareButtons()
    
    // Reconnect with the new terminal
    this.reconnectWithNewTerminal()
  }

  // Update the state of hardware-dependent buttons
  updateHardwareButtons() {
    const hasTerminal = this.terminalIdValue && this.terminalIdValue.length > 0
    
    // Find all buttons that depend on hardware
    const hardwareButtons = document.querySelectorAll('[data-requires-terminal]')
    
    hardwareButtons.forEach(button => {
      if (hasTerminal) {
        // Enable hardware buttons
        button.disabled = false
        button.classList.remove('opacity-50', 'cursor-not-allowed')
        button.removeAttribute('title')
      } else {
        // Disable hardware buttons
        button.disabled = true
        button.classList.add('opacity-50', 'cursor-not-allowed')
        button.title = 'Veuillez sélectionner un terminal pour utiliser cette fonctionnalité'
      }
    })
  }

  reconnectWithNewTerminal() {
    console.log("Reconnecting terminal controller with new terminal:", this.terminalIdValue)
    
    // Disconnect existing subscription
    if (this.hubStatusSubscription) {
      this.hubStatusSubscription.unsubscribe()
    }
    
    // Create new subscription with updated terminal ID
    this.createHubStatusSubscription()
  }

  createHubStatusSubscription() {
    // Only create subscription if we have a terminal ID
    if (!this.terminalIdValue || this.terminalIdValue.length === 0) {
      console.log("No terminal ID, skipping HubChannel subscription")
      return
    }
    
    console.log("Creating HubChannel subscription for terminal...")
    this.hubStatusSubscription = consumer.subscriptions.create(
      {
        channel: "HubChannel",
        terminal_id: this.terminalIdValue,
        client_type: "browser"
      },
      {
        connected() {
          console.log("Terminal HubChannel connected!")
        },
        
        disconnected() {
          console.log("Terminal HubChannel disconnected!")
        },
        
        rejected() {
          console.log("Terminal HubChannel subscription rejected!")
        },
        
        received: (data) => {
          console.log("Terminal HubChannel received data:", data)
          console.log("terminal_status value:", data.terminal_status)
          console.log("terminal_status type:", typeof data.terminal_status)
          console.log("Is ready?:", data.terminal_status === 'ready')
          // Passer l'identifiant du terminal pour que seul le bon badge soit mis à jour
          this.updateBadgeStatus(data.terminal_status === 'ready', this.terminalIdValue)
        }
      }
    )
    console.log("Terminal HubChannel subscription created:", this.hubStatusSubscription)
  }

  updateBadgeStatus(isReady, terminalIdentifier = null) {
    console.log(`updateBadgeStatus called with: ${isReady} (type: ${typeof isReady}), terminal: ${terminalIdentifier}`);
    console.log(`Lecteur de badge ${isReady ? 'prêt' : 'indisponible'}`);
    
    // Si un identifiant de terminal est fourni, ne mettre à jour que ce badge
    let indicatorsToUpdate = this.badgeStatusTargets;
    if (terminalIdentifier) {
      indicatorsToUpdate = this.badgeStatusTargets.filter(indicator => 
        indicator.dataset.terminalIdentifier === terminalIdentifier
      );
      console.log(`Filtering badges for terminal ${terminalIdentifier}:`, indicatorsToUpdate.length, 'found');
    }
    
    // Update only the relevant badge status indicators
    indicatorsToUpdate.forEach(indicator => {
      console.log("Updating badge status indicator:", indicator, "for terminal:", indicator.dataset.terminalIdentifier);
      
      // Remove all possible status classes and animation
      indicator.classList.remove('bg-red-500', 'bg-green-500', 'bg-yellow-500', 'bg-gray-300', 'animate-pulse');
      
      if (isReady) {
        indicator.classList.add('bg-green-500');
        indicator.title = "Terminal connecté et opérationnel";
      } else {
        indicator.classList.add('bg-red-500');
        indicator.title = "Terminal indisponible";
      }
      console.log("Final indicator classes:", indicator.className);
    });
    
    if (indicatorsToUpdate.length === 0) {
      console.error(`No badge status indicators found${terminalIdentifier ? ' for terminal ' + terminalIdentifier : ''}!`);
    }
  }

  // Met à jour l'interface utilisateur pour un terminal spécifique
  updateTerminalUI(data) {
    const terminalId = data.terminal_id
    const terminalIdentifier = data.terminal_identifier
    const terminalCard = this.element.querySelector(`[data-terminal-id="${terminalId}"]`)
    
    if (!terminalCard) {
      console.log(`Terminal card not found for terminal ${terminalId}`)
      // Même si on ne trouve pas de carte, on peut mettre à jour les badges individuels
      const isReady = data.physical_status === 'ready'
      this.updateBadgeStatus(isReady, terminalIdentifier)
      return
    }

    // Mettre à jour le statut logique (disponible/occupé)
    this.updateLogicalStatus(terminalCard, data)
    
    // Mettre à jour le statut physique (en ligne/hors ligne)
    this.updatePhysicalStatus(terminalCard, data)
    
    // Mettre à jour les informations utilisateur
    this.updateUserInfo(terminalCard, data)
    
    // Mettre à jour les badges individuels de ce terminal
    const isReady = data.physical_status === 'ready'
    this.updateBadgeStatus(isReady, terminalIdentifier)
  }

  // Met à jour le statut du terminal actuel dans la navbar si c'est celui qui est sélectionné
  updateCurrentTerminalStatus(data) {
    // Vérifier si la mise à jour concerne le terminal actuellement sélectionné
    const currentTerminalId = this.terminalIdValue
    if (!currentTerminalId || data.terminal_identifier !== currentTerminalId) {
      console.log(`Update not for current terminal. Current: ${currentTerminalId}, Update for: ${data.terminal_identifier}`)
      return
    }

    console.log(`Updating current terminal status in navbar for terminal ${currentTerminalId}:`, data)
    
    // Mettre à jour les indicateurs de badge status pour le terminal spécifique
    const isReady = data.physical_status === 'ready'
    this.updateBadgeStatus(isReady, data.terminal_identifier)
  }

  updateLogicalStatus(terminalCard, data) {
    const isAvailable = data.logical_status === 'available'
    const isOccupied = data.logical_status === 'occupied'
    
    // Activer/désactiver le bouton de sélection
    const selectButton = terminalCard.querySelector('.terminal-select-button')
    if (selectButton) {
      selectButton.disabled = !isAvailable
      
      if (isAvailable) {
        selectButton.classList.remove('opacity-50', 'cursor-not-allowed')
        selectButton.classList.add('hover:bg-green-600')
      } else {
        selectButton.classList.add('opacity-50', 'cursor-not-allowed')
        selectButton.classList.remove('hover:bg-green-600')
      }
    }
    
    // Mettre à jour les classes de style de la carte
    if (isAvailable) {
      terminalCard.classList.remove('bg-red-50', 'border-red-200')
      terminalCard.classList.add('bg-green-50', 'border-green-200')
    } else if (isOccupied) {
      terminalCard.classList.remove('bg-green-50', 'border-green-200')
      terminalCard.classList.add('bg-red-50', 'border-red-200')
    }
    
    // Mettre à jour l'indicateur de statut
    const statusBadge = terminalCard.querySelector('.status-badge')
    if (statusBadge) {
      if (isAvailable) {
        statusBadge.textContent = 'Disponible'
        statusBadge.className = 'status-badge inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800'
      } else if (isOccupied) {
        statusBadge.textContent = 'Occupé'
        statusBadge.className = 'status-badge inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800'
      }
    }
  }

  updatePhysicalStatus(terminalCard, data) {
    const physicalIndicator = terminalCard.querySelector('.physical-status-indicator')
    if (!physicalIndicator) return
    
    // Réinitialiser les classes
    physicalIndicator.classList.remove('bg-green-500', 'bg-red-500', 'bg-yellow-500', 'bg-gray-500')
    
    switch (data.physical_status) {
      case 'ready':
        physicalIndicator.classList.add('bg-green-500')
        physicalIndicator.title = 'Terminal connecté et opérationnel'
        break
      case 'error':
        physicalIndicator.classList.add('bg-yellow-500')
        physicalIndicator.title = 'Terminal connecté mais avec des erreurs'
        break
      case 'offline':
      default:
        physicalIndicator.classList.add('bg-red-500')
        physicalIndicator.title = 'Hub hors ligne'
        break
    }
  }

  updateUserInfo(terminalCard, data) {
    const userInfo = terminalCard.querySelector('.user-info')
    if (!userInfo) return
    
    if (data.user_name) {
      userInfo.textContent = `Utilisé par ${data.user_name}`
      userInfo.classList.remove('hidden')
    } else {
      userInfo.classList.add('hidden')
    }
  }
}
